import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, User } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";

export default function HostsPage() {
  const [team, setTeam] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => setTeam([])); }, []);

  const visible = useMemo(() => {
    const list = team || [];
    const needle = q.trim().toLowerCase();
    return needle ? list.filter((m) => m.name.toLowerCase().includes(needle) || (m.show_names || []).some((s) => s.toLowerCase().includes(needle))) : list;
  }, [team, q]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="hosts-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-32">
        <Link to="/#team" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="hosts-back-link"><ArrowLeft size={16} /> Back to home</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow mb-2">The pack</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none" data-testid="hosts-title">Our Hosts</h1>
            {team && <p className="mt-3 text-sm text-slate-400" data-testid="hosts-count">{team.length} podcasters across the Underdog network</p>}
          </div>
          <div className="relative w-full md:w-72">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a host or show…" className="pl-9 bg-white/5 border-white/10" data-testid="hosts-filter-input" />
          </div>
        </div>

        {!team ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">{[...Array(10)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl bg-white/5" />)}</div>
        ) : visible.length === 0 ? (
          <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="hosts-empty">{q ? `No hosts match “${q}”.` : "No hosts yet."}</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="hosts-grid">
            {visible.map((m, i) => (
              <Link key={m.id} to={`/hosts/${m.slug}`} className="on-dark group relative aspect-[3/4] rounded-2xl overflow-hidden card-surface reveal" style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }} data-testid={`hosts-card-${m.slug}`}>
                {m.image_url ? <img src={imgUrl(m.image_url)} alt={m.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading={i < 10 ? "eager" : "lazy"} /> : <div className="absolute inset-0 bg-[#053671] flex items-center justify-center"><User size={48} className="text-sky-200" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070F1E] via-[#070F1E]/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="font-display font-bold text-lg leading-tight text-white" data-testid={`hosts-card-name-${m.slug}`}>{m.name}</p>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-sky-400 mt-0.5">{m.role}</p>
                  {m.show_names?.length > 0 && <p className="mt-1.5 text-xs text-slate-300 line-clamp-2">{m.show_names.join(" · ")}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
